import { useQuery } from '@tanstack/react-query';
import { request } from '@/lib/http';
import { isInFlight, type IssuanceState } from '@/features/invoices/outbound/api';
import { BADGE_COUNTS_QUERY_KEY } from './queryKeys';

/**
 * Sidebar badge-count client. Mirrors the Ktor `/api/v1/badge-counts` contract 1:1
 * (`apps/api/.../routes/badges/model/BadgeCountsResponse.kt`): one aggregate read backs every sidebar
 * badge — the started-draft count and the outbound register bucketed by issuance state. The whole
 * surface is dark unless `me.issuingEnabled` (the endpoint 404s when issuing is off), so only mount the
 * consuming badges inside the issuing nav group, which is already gated on that flag.
 */

export { BADGE_COUNTS_QUERY_KEY };

/** Wire shape of `/api/v1/badge-counts`. */
export interface BadgeCounts {
  /** Started (non-pristine) ACTIVE drafts — the pristine "new invoice" placeholder is excluded server-side. */
  drafts: number;
  /** Per-state outbound counts. The API zero-fills every state; a missing key is treated as 0 defensively. */
  outbound: Partial<Record<IssuanceState, number>>;
}

/**
 * The sidebar badge counts. Poll cadence tracks volatility: ~10s while any outbound invoice sits in a
 * self-advancing state (`isInFlight` — the counts are about to move on their own), ~60s once everything
 * has settled. Discrete user actions (delete / issue / retry / abandon / resend) invalidate this key for
 * an instant refresh; the poll is only the backstop for background transitions and other tabs.
 */
export function useBadgeCounts() {
  return useQuery<BadgeCounts>({
    queryKey: BADGE_COUNTS_QUERY_KEY,
    queryFn: () => request<BadgeCounts>('/api/v1/badge-counts'),
    refetchInterval: (query) => {
      const outbound = query.state.data?.outbound;
      if (!outbound) return 60_000;
      const anyInFlight = (Object.keys(outbound) as IssuanceState[]).some(
        (state) => isInFlight(state) && (outbound[state] ?? 0) > 0,
      );
      return anyInFlight ? 10_000 : 60_000;
    },
    staleTime: 5_000,
    retry: false,
  });
}
